'use client';

import classNames from 'classnames';
import { useEffect, useState } from 'react';

import { EVENT_NAMES, trackEvent } from '@/analytics';
import CaretUp from '@/icons/react/CaretUp';

export interface BackToTopButtonProps {
  visibilityThreshold?: number;
}

export function BackToTopButton({
  visibilityThreshold = 700,
}: BackToTopButtonProps) {
  const [isVisible, setIsVisible] = useState(false);

  function handleScroll() {
    if (window.scrollY > visibilityThreshold) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }

  function handleClick() {
    window.scrollTo({ top: 0, behavior: 'smooth' });

    function getScrollPercentage() {
      const documentHeight = document.documentElement.scrollHeight;
      const viewportHeight = window.innerHeight;
      const scrollDistance =
        window.scrollY || document.documentElement.scrollTop;

      const scrollPercentage =
        (scrollDistance / (documentHeight - viewportHeight)) * 100;

      // Ensure it returns 100% when fully scrolled
      return Math.min(scrollPercentage, 100);
    }

    trackEvent(EVENT_NAMES.back_to_top_click, {
      scrolltop_distance_px: window.scrollY.toString(),
      scrolltop_distance_percent: getScrollPercentage().toString(),
    });
  }

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
    // biome-ignore lint/correctness/useExhaustiveDependencies: todo
  }, [handleScroll]);

  return (
    <button
      type="button"
      onClick={handleClick}
      className={classNames(
        'opacity-0',
        isVisible ? 'xl:opacity-100' : 'pointer-events-none',
        'fixed bottom-10 right-10 w-12 h-12 rounded-full bg-paper shadow-lg flex items-center justify-center border border-primary',
        'transition-all',
        'hover:bg-gray-100 hover:scale-105',
      )}
    >
      <span className="sr-only">Back to top</span>
      <CaretUp className="w-6 h-6 text-primary" />
    </button>
  );
}
